/**
 * Date utilities for timeline services
 */

import { coerceCalendarDate, getCalendarDateKey } from '../../utils/calendarDateKey';
import { extractCareTimeFromText } from '../../shared/careDateTimePatterns';

/**
 * Get date range for a specific day
 * @param {Date} date - Target date
 * @returns {Object} - { start: Date, end: Date }
 */
export const getDayDateRange = (date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  
  return { start, end };
};

/**
 * Check if a timestamp falls within a date range
 * @param {Date|Object} timestamp - Timestamp to check (Date or Firestore timestamp)
 * @param {Date} start - Start of range
 * @param {Date} end - End of range
 * @returns {boolean}
 */
export const isWithinDateRange = (timestamp, start, end) => {
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  return date >= start && date <= end;
};

const normalizeEntrySource = (entry = {}) => entry?.originalData || entry?.rawData || entry;

const normalizeModeValue = (value) => String(value || '').trim().toLowerCase();

const parseLocalTimeParts = (value = '') => {
  const match = String(value || '').trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) {
    return null;
  }

  const [, hourText, minuteText = '0', periodText = ''] = match;
  let hours = Number(hourText);
  const minutes = Number(minuteText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
    return null;
  }

  const period = periodText.toUpperCase();
  if (period === 'PM' && hours < 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  return hours >= 0 && hours <= 23 ? { hours, minutes } : null;
};

const buildLocalDateTime = (dateValue, timeValue) => {
  const date = coerceCalendarDate(dateValue);
  const timeParts = parseLocalTimeParts(timeValue);
  if (!date || !timeParts) {
    return null;
  }

  const resolved = new Date(date);
  resolved.setHours(timeParts.hours, timeParts.minutes, 0, 0);
  return resolved;
};

const getStoredEntryLocalTime = (entry = {}, source = {}) => (
  source?.localTime
    || source?.time
    || source?.activityDetails?.localTime
    || source?.foodDetails?.localTime
    || source?.sleepDetails?.localTime
    || source?.bathroomDetails?.localTime
    || source?.medicationDetails?.localTime
    || source?.incidentData?.localTime
    || entry?.localTime
    || entry?.time
    || entry?.activityDetails?.localTime
    || entry?.foodDetails?.localTime
    || entry?.sleepDetails?.localTime
    || entry?.bathroomDetails?.localTime
    || entry?.medicationDetails?.localTime
    || entry?.incidentData?.localTime
    || ''
);

const getCaptureFirstTimeText = (entry = {}, source = {}) => [
  source?.metadata?.sourceSpanText,
  source?.source?.sourceSpanText,
  source?.sourceText,
  source?.originalText,
  source?.body,
  source?.text,
  source?.note,
  source?.content,
  entry?.metadata?.sourceSpanText,
  entry?.source?.sourceSpanText,
  entry?.sourceText,
  entry?.originalText,
  entry?.body,
  entry?.text,
  entry?.note,
  entry?.content,
  entry?.description,
  entry?.summary,
].filter(Boolean).join(' ');

const getEntryLocalTime = (entry = {}, source = {}, allowTextFallback = false) => {
  const storedTime = getStoredEntryLocalTime(entry, source);
  if (storedTime) {
    return storedTime;
  }

  if (!allowTextFallback) {
    return '';
  }

  return extractCareTimeFromText(getCaptureFirstTimeText(entry, source)).time || '';
};

const isCaptureFirstCalendarEntry = (entry = {}, source = {}) => {
  const metadata = source?.metadata || entry?.metadata || {};
  const sourceObject = source?.source || entry?.source || {};
  const captureFirst = metadata.captureFirst || entry?.captureFirst || source?.captureFirst || null;
  const parserMode = normalizeModeValue(source?.parserMode || entry?.parserMode || metadata.parserMode);
  const inputMode = normalizeModeValue(source?.inputMode || entry?.inputMode || metadata.inputMode || sourceObject.mode);
  const sourceMode = normalizeModeValue(source?.sourceMode || entry?.sourceMode || metadata.sourceMode || sourceObject.type);
  const timestampSource = normalizeModeValue(source?.timestampSource || entry?.timestampSource || metadata.timestampSource);
  const extractedBy = normalizeModeValue(sourceObject.extractedBy || source?.extractedBy || entry?.extractedBy);

  return Boolean(
    captureFirst
    || parserMode === 'capture_first'
    || parserMode === 'capture_first_backend_ai'
    || inputMode === 'capture_first'
    || sourceMode === 'quick_log_capture_first'
    || sourceMode === 'file_import_capture_first'
    || timestampSource.startsWith('capture-first')
    || extractedBy === 'capture_first_backend_ai'
  );
};

const MONTH_INDEX = {
  january: 0,
  jan: 0,
  february: 1,
  feb: 1,
  march: 2,
  mar: 2,
  april: 3,
  apr: 3,
  may: 4,
  june: 5,
  jun: 5,
  july: 6,
  jul: 6,
  august: 7,
  aug: 7,
  september: 8,
  sep: 8,
  sept: 8,
  october: 9,
  oct: 9,
  november: 10,
  nov: 10,
  december: 11,
  dec: 11,
};

const LEADING_DATE_PATTERN = /^(?:(?:mon|tues|wednes|thurs|fri|satur|sun)day,?\s+)?(?<month>jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(?<day>\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(?<year>\d{4}))?\b/i;

const resolveLeadingTextDate = (value = '', anchorDate = new Date()) => {
  const match = String(value || '').trim().match(LEADING_DATE_PATTERN);
  if (!match?.groups) {
    return null;
  }

  const month = MONTH_INDEX[match.groups.month.toLowerCase().replace(/\.$/, '')];
  const day = Number(match.groups.day);
  const explicitYear = Number(match.groups.year);
  if (!Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  const anchor = coerceCalendarDate(anchorDate) || new Date();
  const date = new Date(Number.isFinite(explicitYear) ? explicitYear : anchor.getFullYear(), month, day);
  if (!Number.isFinite(explicitYear) && date > anchor) {
    date.setFullYear(date.getFullYear() - 1);
  }

  return Number.isNaN(date.getTime()) ? null : date;
};

const getCaptureFirstTextDate = (entry = {}, source = {}) => {
  const captureFirst = source?.metadata?.captureFirst || entry?.metadata?.captureFirst || {};
  if (!captureFirst?.enabled || (source?.timeSource || entry?.timeSource) === 'manual') {
    return null;
  }

  return resolveLeadingTextDate(
    source?.metadata?.sourceSpanText
    || source?.source?.sourceSpanText
    || source?.text
    || source?.body
    || entry?.metadata?.sourceSpanText
    || entry?.source?.sourceSpanText
    || entry?.text
    || entry?.body
    || ''
  );
};

export const getTimelineEntryDate = (entry) => {
  const source = normalizeEntrySource(entry);
  const sleepDetails = source?.sleepDetails || entry?.sleepDetails || {};
  const isSleepEntry = source?.category === 'sleep'
    || source?.source === 'sleep_log'
    || source?.type === 'sleep'
    || source?.timelineType === 'sleep'
    || source?.timestampSource === 'sleep-anchor'
    || entry?.timestampSource === 'sleep-anchor'
    || Boolean(sleepDetails.startAt || sleepDetails.endAt || sleepDetails.bedtime || sleepDetails.wakeTime || sleepDetails.durationHours);

  const fullDateTimeCandidates = [
    source?.timestamp?.toDate?.(),
    source?.timestamp,
    source?.createdAt?.toDate?.(),
    source?.createdAt,
    source?.date?.toDate?.(),
    source?.date,
    entry?.timestamp?.toDate?.(),
    entry?.timestamp,
    entry?.createdAt?.toDate?.(),
    entry?.createdAt,
    entry?.date?.toDate?.(),
    entry?.date,
  ];

  const calendarOnlyCandidates = [
    source?.entryDayKey,
    source?.incidentDayKey,
    source?.dayKey,
    source?.localDayKey,
    source?.dateKey,
    source?.localDate,
    source?.activityDetails?.localDate,
    source?.foodDetails?.localDate,
    source?.sleepDetails?.localDate,
    source?.bathroomDetails?.localDate,
    source?.medicationDetails?.localDate,
    source?.incidentData?.localDate,
    entry?.entryDayKey,
    entry?.incidentDayKey,
    entry?.dayKey,
    entry?.localDayKey,
    entry?.dateKey,
    entry?.localDate,
    entry?.activityDetails?.localDate,
    entry?.foodDetails?.localDate,
    entry?.sleepDetails?.localDate,
    entry?.bathroomDetails?.localDate,
    entry?.medicationDetails?.localDate,
    entry?.incidentData?.localDate,
  ];

  const captureFirstTextDate = getCaptureFirstTextDate(entry, source);
  const shouldPreferCalendarOnlyDate = isCaptureFirstCalendarEntry(entry, source);
  const localTime = getEntryLocalTime(entry, source, shouldPreferCalendarOnlyDate);
  const captureFirstTextDateTime = buildLocalDateTime(captureFirstTextDate, localTime);
  const calendarDateTimeCandidates = shouldPreferCalendarOnlyDate
    ? calendarOnlyCandidates.map((candidate) => buildLocalDateTime(candidate, localTime)).filter(Boolean)
    : [];

  const candidates = [
    ...(isSleepEntry
    ? [
        sleepDetails.displayDate,
        source?.displayDate,
        sleepDetails.endAt,
        sleepDetails.endTime,
        sleepDetails.wakeTime,
        source?.endAt,
        source?.endTime,
        source?.wakeTime,
        sleepDetails.startAt,
        sleepDetails.anchorDate,
        sleepDetails.localDate,
        source?.startAt,
        source?.anchorDate,
        source?.localDate,
        source?.entryDate,
        sleepDetails.startTime,
        sleepDetails.bedtime,
        ...fullDateTimeCandidates,
        ...calendarOnlyCandidates,
      ]
    : [
        captureFirstTextDateTime,
        ...calendarDateTimeCandidates,
        captureFirstTextDate,
        ...(shouldPreferCalendarOnlyDate ? calendarOnlyCandidates : fullDateTimeCandidates),
        ...(shouldPreferCalendarOnlyDate ? fullDateTimeCandidates : calendarOnlyCandidates),
      ]),
  ];

  for (const candidate of candidates) {
    const date = coerceCalendarDate(candidate);
    if (date) {
      return date;
    }
  }

  return null;
};

export const getTimelineEntryDateKey = (entry) => {
  const date = getTimelineEntryDate(entry);
  if (!date) {
    return '';
  }

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

export const isTimelineEntryOnDate = (entry, selectedDate) => {
  const entryDateKey = getTimelineEntryDateKey(entry);
  const selectedDateKey = getCalendarDateKey(selectedDate);
  return Boolean(entryDateKey && selectedDateKey && entryDateKey === selectedDateKey);
};

export const isTimelineEntryVisibleOnDate = (entry, selectedDate, start, end) => {
  const entryDateKey = getTimelineEntryDateKey(entry);
  const selectedDateKey = getCalendarDateKey(selectedDate);
  if (entryDateKey && selectedDateKey) {
    return entryDateKey === selectedDateKey;
  }

  return isWithinDateRange(entry?.timestamp, start, end);
};
